import React, { useState, useEffect, useRef, useCallback } from 'react';
import IdleScreen from '../Components/kiosk/IdleScreen';
import OrderTypeSelector from '../Components/kiosk/OrderTypeSelector';
import MenuBrowser from '../Components/kiosk/MenuBrowser';
import Cart from '../Components/kiosk/Cart';
import Checkout from '../Components/kiosk/Checkout';
import OrderConfirmation from '../Components/kiosk/OrderConfirmation';
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { useRestaurantInfo } from "../Hooks/useRestaurantInfo";
//import { getConnectionToken } from "../services/api";
import { useAuthToken } from "../Hooks/useAuthToken";
import { subscribeToRestaurantUpdates } from '../services/subscriptionService';
import { initTerminal, connectToReader } from "../services/stripeTerminal";
import { isWrapperMode } from "../services/wrapperService";
import { setStep } from '../redux/Slices/stepSlice';
import { setError as setStoreError } from '../redux/Slices/restaurantSlice';
import { useConfig } from "../App";
//import { combineSlices } from '@reduxjs/toolkit';
const IDLE_TIMEOUT = 900000; // 15 minutes

export default function Kiosk() {
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  // get restaurant id and name 
  const { restaurantName, restaurantId } = useParams();
  const restaurantConfig = useSelector((state) => state.restaurantConfig.restaurantConfig);
  const restaurantMetadata = useSelector((state) => state.restaurantMetadata.data);
  const step = useSelector((state) => state.step.status);
  const initialized = useRef(false);
  const cfg = useConfig();
  // initate auth and call  restaurant api 
  const { getToken } = useAuthToken();
  const { getrestaurantData, getRestaurantConfigData, syncRestaurantData, updateRestaurantInfo } = useRestaurantInfo();
  const [retryCount, setRetryCount] = useState(0);
  const errorRest = useSelector((state) => state.restaurant.error);
  // auto retry logic for wrapper mode
  useEffect(() => {
    if ((error || errorRest) && isWrapperMode() === "yes") {
      console.log("Error detected in wrapper mode, scheduling auto-retry...");
      const timer = setTimeout(() => {
        console.log("Auto-retrying initialization (Retry #" + (retryCount + 1) + ")");
        initialized.current = false;
        setError(null);
        dispatch(setStoreError(null));
        setRetryCount(prev => prev + 1);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, errorRest, dispatch, retryCount]);

  useEffect(() => {
    //get reader settings
    const readerSettings = cfg.config.ReaderSettings.find(
      r => r.restaurant_id === restaurantId
    );
    console.log("Reader Settings: ", readerSettings);
    const locationId = readerSettings?.locationId ?? null;
    let simulated = false;
    const readerMode = readerSettings?.reader_mode ?? "simulated";
    if (readerMode === "simulated") {
      simulated = true;
    } else if (readerMode === "real") {
      simulated = false;
    }
    if (initialized.current) {
      return;
    }
    initialized.current = true;
    // initiate restaurant
    const initRestaurant = async () => {
      try {
        if (!restaurantId) {
          console.warn("Waiting for restaurantId...");
          return; // Don't proceed if we don't have an ID yet
        }
        const accessTokenData = await getToken();
        const wrapperMode = isWrapperMode();
        //call connection token api if wrapper mode is enabled
        if (wrapperMode === "yes") {
          console.log("Location ID: " + locationId);
          //send a post message to wrapper with connection token and location id
          const messageData = {
            type: "AUTH_TOKEN_EVENT",
            accessToken: accessTokenData.accessToken,
            locationId: locationId,
            simulated: simulated
          }
          console.log("sending message data: " + JSON.stringify(messageData));
          if (window.ReactNativeWebView) {
            let msgno = 1;
            window.ReactNativeWebView.postMessage(JSON.stringify(messageData));
            console.log("Sent message to RN WebView msgno: " + msgno + ":", messageData);
            msgno++;
          } else {
            console.warn("ReactNativeWebView not found");
          }

        }
        // setup terminal only if not in wrapper mode
        if (wrapperMode === "no") {
          const terminal = await initTerminal(accessTokenData.accessToken);
          if (!terminal) {
            console.error('Failed to initialize terminal');
            setError('Failed to initialize terminal');
          }

          //connect reader
          const connectionResult = await connectToReader({
            simulated: simulated,
            locationId: locationId
          });
          if (connectionResult.status === 'connected' || connectionResult.status === 'online') {
            console.log('Connected to reader');
          } else {
            console.error('Failed to connect to reader');
            setError(connectionResult.message);
          }
        }
        //call restaruant data api
        await getrestaurantData(accessTokenData, restaurantId);

        // get realtime sync config details
        await getRestaurantConfigData(accessTokenData.accessToken, restaurantId);
      }
      catch (err) {
        console.error("API failed:", err);
        setError("Something went wrong while loading restaurant data.");
      }
    };
    initRestaurant();
  }, [restaurantId, retryCount])

  // subscribe to realtime updates
  useEffect(() => {
    if (!restaurantConfig?.kiosk_hashed_id) return;

    console.log("Setting up subscription with config");
    const unsubscribe = subscribeToRestaurantUpdates(
      restaurantConfig.kiosk_hashed_id,
      restaurantConfig.realtime_update,
      syncRestaurantData
    );

    return unsubscribe;
  }, [restaurantConfig, syncRestaurantData]);

  const [orderType, setOrderType] = useState(null);
  const [cart, setCart] = useState([]);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [receiptPayload, setReceiptPayload] = useState(null);
  const inactivityTimer = useRef(null);
  const [customizingItem, setCustomizingItem] = useState(null);
  const [isCancel, setIsCancel] = useState(false);
  const [cancelSource, setCancelSource] = useState(null);
  const restaurant_info = useSelector((state) => state.restaurant.restaurantInfo);

  const categories = useSelector((state) => state.category.categories);
  const MenuItem = useSelector((state) => state.menu.menu_items);
  const menuItems = MenuItem.flatMap(category =>
    category.menu_details
      .filter(item => item.menu_in_stock)
      .map(item => ({
        ...item,
        category_id: category.category_id,
        category_name: category.category_name
      }))
  );
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }
    if (step !== 'idle' && step !== 'confirmation') {
      inactivityTimer.current = setTimeout(() => {
        handleReset();
      }, IDLE_TIMEOUT);
    }
  }, [step]);
  useEffect(() => {
    resetInactivityTimer();
    return () => {
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    };
  }, [resetInactivityTimer]);
  const handleReset = () => {
    dispatch(setStep("idle"));
    setOrderType(null);
    setCart([]);
    setCompletedOrder(null);
  };
  const handleStartOrder = () => {
    dispatch(setStep("order_type"));
    resetInactivityTimer();
  };
  const handleOrderTypeSelect = (type) => {
    //console.log("type", type);
    setOrderType(type);
    dispatch(setStep("menu"));
    resetInactivityTimer();
  };
  const handleAddToCart = (item) => {
    // console.log("Adding to cart:", item);
    setCart(prev => {
      const existing = prev.find(
        i => i.item_id === item.item_id &&
          JSON.stringify(i.customizations) === JSON.stringify(item.customizations) &&
          i.instructions === item.instructions
      );
      if (existing) {
        return prev.map(i =>
          i === existing ? { ...i, quantity: i.quantity + 1, subtotal: (i.quantity + 1) * i.price } : i
        );
      }
      return [...prev, { ...item, cart_item_id: `${Date.now()}_${Math.random()}`, quantity: item.quantity, subtotal: item.price * item.quantity }];
    });
    resetInactivityTimer();
  };
  const handleUpdateCartItems = (updatedItem, originalCartItemId) => {
    console.log("updatedItem", updatedItem);
    setCart(prev => {
      // Find the item being updated by its cart ID
      const itemIndex = prev.findIndex(i => i.cart_item_id === originalCartItemId);

      if (itemIndex === -1) {
        console.error('Cart item not found');
        return prev;
      }

      // Check if the updated customizations match any OTHER existing cart item
      const duplicateItem = prev.find(
        (i, idx) =>
          idx !== itemIndex && // Don't compare with itself
          i.item_id === updatedItem.item_id &&
          JSON.stringify(i.customizations) === JSON.stringify(updatedItem.customizations) &&
          i.instructions === updatedItem.instructions
      );

      if (duplicateItem) {
        // Merge with existing duplicate: remove the old item, increase quantity of duplicate
        return prev
          .filter((_, idx) => idx !== itemIndex)
          .map(i =>
            i.cart_item_id === duplicateItem.cart_item_id
              ? {
                ...i,
                quantity: i.quantity + updatedItem.quantity,
                subtotal: (i.quantity + updatedItem.quantity) * i.price
              }
              : i
          );
      } else {
        // No duplicate found: just update the item in place
        return prev.map((i, idx) =>
          idx === itemIndex
            ? {
              ...i,
              ...updatedItem,
              cart_item_id: i.cart_item_id,
              subtotal: updatedItem.quantity * updatedItem.price
            }
            : i
        );
      }
    });

    resetInactivityTimer();
  };
  const handleRemoveFromCart = (cartItemId) => {
    setCart(prev => {
      // Find the last index of the item with the given ID
      const lastIndex = prev.map(item => item.cart_item_id).lastIndexOf(cartItemId);
      if (lastIndex === -1) return prev;

      const item = prev[lastIndex];
      // if (item.quantity > 1) {
      //   return prev.map((i, idx) =>
      //     idx === lastIndex ? { ...i, quantity: i.quantity - 1, subtotal: (i.quantity - 1) * i.price } : i
      //   );
      // }
      return prev.filter((_, idx) => idx !== lastIndex);
    });
    resetInactivityTimer();
  };

  const handleUpdateCart = (index, quantity) => {
    if (quantity === 0) {
      setCart(prev => prev.filter((_, i) => i !== index));
    } else {
      setCart(prev =>
        prev.map((item, i) =>
          i === index ? { ...item, quantity, subtotal: quantity * item.price } : item
        )
      );
    }
    resetInactivityTimer();
  };

  // apply restaurant updates to cart
  const handleApplyUpdates = (diff) => {
    if (!restaurantMetadata || !restaurantMetadata.menu_items) return;
    // console.log("cart before applying updates", cart);
    // console.log("DIFF RECEIVED IN APPLY UPDATES", diff);

    setCart((prevCart) => {
      // --------------------------------
      // 1. Build a lookup map for menu items
      // --------------------------------
      const menuMap = new Map();

      restaurantMetadata.menu_items.forEach((category) => {
        category.menu_details.forEach((menuItem) => {
          menuMap.set(menuItem.menu_id, menuItem);
        });
      });

      // --------------------------------
      // 2. Update every cart item
      // --------------------------------
      const updatedCart = prevCart
        .map((cartItem) => {
          const latest = menuMap.get(cartItem.item_id);

          // Find specific updates for this item in the diff
          const itemUpdate = diff?.itemsUpdated?.find(u => u.id === cartItem.item_id);

          // Remove if item does not exist OR is out of stock
          if (!latest || latest.menu_in_stock === false) return null;

          const basePrice = Number(latest.menu_item_price) || 0;
          const newitemname = cartItem.item_name !== latest.menu_item_name;
          // --------------------------------
          // 3. Rebuild customizations & sub-modifiers with updated names/prices
          // --------------------------------
          //console.log("LATEST", latest);
          const newCustomizations = (cartItem.customizations || [])
            .map((cust) => {
              const latestGroup = latest.options?.find(
                (g) => g.id === cust.groupId
              );
              if (!latestGroup) return null;

              const latestOption = latestGroup.option?.find(
                (o) => o.opt_id === cust.optionId
              );
              //console.log("latestOption", latestOption);
              if (!latestOption) return null;

              const optionPrice = Number(latestOption.price) || 0;

              // Check for quantity correction in diff
              const qtyCorrection = itemUpdate?.modifierQtyChanges?.find(q => q.name === cust.name);
              const optionQty = qtyCorrection ? qtyCorrection.newQty : (cust.qty || 1);

              // Rebuild sub modifiers
              const newSubModifiers = (cust.subModifiers || [])
                .map((sub) => {
                  const latestSubGroup = latestOption.sub_options?.find(
                    (sg) => sg.id === sub.groupId
                  );
                  //console.log("latestSubGroup", latestSubGroup);
                  if (!latestSubGroup) return null;

                  const latestSubOpt = latestSubGroup.option?.find(
                    (o) => o.opt_id === sub.optionId
                  );
                  //console.log("latestSubOpt", latestSubOpt);
                  if (!latestSubOpt) return null;

                  // Check for sub-modifier quantity correction
                  const subQtyCorrection = itemUpdate?.modifierQtyChanges?.find(q => q.name === sub.name);
                  const subQty = subQtyCorrection ? subQtyCorrection.newQty : (sub.qty || 1);

                  return {
                    ...sub,
                    name: latestSubOpt.name, // update name
                    price: Number(latestSubOpt.price) || 0, // update price
                    qty: subQty
                  };
                })
                .filter(Boolean);

              return {
                ...cust,
                name: latestOption.name, // update modifier name
                price: optionPrice, // update price
                qty: optionQty,
                subModifiers: newSubModifiers,
              };
            })
            .filter(Boolean);

          // --------------------------------
          // 4. Calculate price totals
          // --------------------------------
          const addonsTotal = newCustomizations.reduce((sum, c) => {
            const subTotal = c.subModifiers?.reduce(
              (s, sm) => s + Number(sm.price),
              0
            );
            return sum + Number(c.price) + subTotal;
          }, 0);

          const unitTotal = basePrice + addonsTotal;

          // --------------------------------
          // 5. Build and return updated cart item
          // --------------------------------
          return {
            ...cartItem,

            // Update cart fields from menu
            item_name: newitemname ? latest.menu_item_name : cartItem.item_name,
            item_image: latest.menu_item_image,
            item_description: latest.menu_item_description,
            item_base_price: latest.menu_item_price,
            in_stock: latest.menu_in_stock,
            is_tax_applicable: latest.is_tax_applicable,

            // Updated pricing
            price: basePrice,
            customizations: newCustomizations,
            subtotal: unitTotal * cartItem.quantity,

            // (Optional) keep latest menu options reference
            menu_options: latest.options,
          };
        })
        .filter(Boolean); // Remove null entries

      return updatedCart;
    });

    console.log("CART AFTER UPDATES APPLIED", cart);
    try {
      updateRestaurantInfo();
    } catch (err) {
      console.warn("Failed to call updateRestaurantInfo", err);
    }
  };

  const clearCart = (source) => {
    if (source === "cart") {
      setCart([]);
      dispatch(setStep("idle"));
    }
    if (source === "back") {
      handleReset();
    }
    if (source === "autoCancel") {
      setCart([]);
      dispatch(setStep("idle"));
    }

  };
  const handleProceedToCheckout = () => {
    dispatch(setStep("checkout"));
    resetInactivityTimer();
  };

  const handleOrderComplete = (order, receiptPayload) => {
    setCompletedOrder(order);
    setReceiptPayload(receiptPayload);
    dispatch(setStep('confirmation'));
  };
  const cartTotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  if (error || errorRest) {
    if (isWrapperMode() === "yes") {
      return (
        <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
          <div className="text-center">
            {/* <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-boonsOrange mx-auto mb-12"></div> */}
            <p className="text-2xl font-bold text-gray-800 mb-2">Please wait while we load restaurant info...</p>
            <p className="text-lg text-gray-500"></p>
            {/* <p className="text-sm text-gray-400 mt-4">Automatic retry attempt: {retryCount}</p> */}
          </div>
        </div>
      );
    }
    return (
      <div className="text-center py-12">
        <h2 className="text-3xl font-bold text-red-600 mb-2">Error</h2>
        <p className="text-xl text-gray-600"> {error || errorRest || "Unknown Error"}</p>
        <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-boonsOrange text-white rounded">
          Retry
        </button>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 bg-bordergray overflow-hidden"
      onClick={resetInactivityTimer}
      onTouchStart={resetInactivityTimer}>
      {step === 'idle' && <IdleScreen restaurant_info={restaurant_info} onStart={handleStartOrder} restaurantId={restaurantId} />}

      {step === 'order_type' && (
        <OrderTypeSelector
          onSelect={handleOrderTypeSelect}
          onBack={handleReset}
        />
      )}

      {step === 'menu' && (
        <div className="h-full flex flex-col">
          <MenuBrowser
            categories={categories}
            menuItems={menuItems}
            cart={cart}
            cartCount={cartCount}
            onAddToCart={handleAddToCart}
            onRemoveFromCart={handleRemoveFromCart}
            onReset={() => {
              if (cart.length > 0) {
                setIsCancel(true);   // show modal
              } else {
                handleStartOrder();       // safe reset
              }
            }}
            customizingItem={customizingItem}
            setCustomizingItem={setCustomizingItem}
            cancelSource={cancelSource}
            setCancelSource={setCancelSource}
            onCheckout={handleProceedToCheckout}
          />
          {cart.length > 0 && !customizingItem && (
            <Cart
              cart={cart}
              onUpdateCart={handleUpdateCartItems}
              onRemoveFromCart={handleRemoveFromCart}
              onUpdateCartCount={handleUpdateCart}
              onCheckout={handleProceedToCheckout}
              cartTotal={cartTotal}
              cartCount={cartCount}
              isCancel={isCancel}
              setIsCancel={setIsCancel}
              clearCart={clearCart}
              cancelSource={cancelSource}
              setCancelSource={setCancelSource}
            />
          )}
        </div>
      )}

      {step === 'checkout' && (
        <Checkout
          restaurant_info={restaurant_info}
          cart={cart}
          cartTotal={cartTotal}
          orderType={orderType}
          onApplyUpdates={handleApplyUpdates}
          onComplete={handleOrderComplete}
          onBack={() => dispatch(setStep('menu'))}
          onCancel={clearCart}
          cancelSource={cancelSource}
          setCancelSource={setCancelSource}
        />
      )}

      {step === 'confirmation' && (
        <OrderConfirmation
          order={completedOrder}
          onReset={handleReset}
          receiptPayload={receiptPayload}
        />
      )}
    </div>
  );
}